import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { OPCalendarService } from './calendar.service';
import { OPCalendarDTO, OPCalendarGetMonthlyCalendarByCompanyRequestDTO } from './calendar.interface';
import { catchError, retryWhen, switchMap, timer } from 'rxjs';

 
@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.css']
})
export class CalendarComponent implements OnInit {
 
  @Output() onDayClick: EventEmitter<OPCalendarDTO> = new EventEmitter();
  @Output() sendOPCalender: EventEmitter<OPCalendarDTO[]> = new EventEmitter();
 
 
  currentYear: number = new Date().getFullYear();
  currentMonth: number = new Date().getMonth() + 1; // 1-based index
  @Output() onMonthYearChange: EventEmitter<{ year: number; month: number }> = new EventEmitter();
 
  years: number[] = [];
  monthsWithNumbers = Array.from({ length: 12 }, (_, i) => ({
    number: i + 1,
    name: new Date(2000, i, 1).toLocaleString("default", { month: "long" })
  }));
 
  calendarDays: OPCalendarDTO[] = [];
 
  calendarService = inject(OPCalendarService);
  private latestSubscription: any;
  selectedDays: OPCalendarDTO[] = [];
  private monthChangeTimeout: any;
 

  @Input() allowClickingFutureDates: boolean = false;
  @Input() hasTocheckRG: boolean = false;
 
  //ORANGE BORDER
  @Input() hasOrangeBorderDates: OPCalendarDTO[] = [];
  @Input() showOrangeBorder: boolean = false;
 
  //TICK MARK
  @Input() showTickMark: boolean = false;

 
  //CROSS MARK
  @Input() showCrossMark: boolean = false;
 
  //TIME ICON
  @Input() showTimeIcon: boolean = false;

 
  //IS PRESENT
  @Input() showIsPresent: boolean = true;
  @Input() showLegends : boolean = false;
 
  //SHOW NOTIFICATION FOR CALENDAR
  @Input() showNotification: boolean = false;
 
  @Input() notificationMsg: { notiMsg: string; notiBtnMsg: string } = { notiMsg: '', notiBtnMsg: '' };
  @Output() onNotiBtnClicked: EventEmitter<void> = new EventEmitter();
 
  @Input() isNavigationDisabled: boolean = false; // New input to control navigation
  @Output() emitErrorMsg: EventEmitter<string> = new EventEmitter();
 
  @Input() numberIndicatorData: { date: string; count: number; tooltip?: string[]}[] = [];
  @Input() showNumberIndicatorData: boolean = false;
  @Input() allowMultipleSelection: boolean = true;
  @Input() showInitialUserIcons: boolean = true;
  @Input() initialUserIconsData: { date: string, username: string[], tooltip?: string[] }[] = [];
  @Input() isHighlightTodaysDay: { enabled: boolean, style: 'dot' | 'underline' | 'border' | 'fill' } = { enabled: true, style: 'dot' };
  attendanceDates: Set<string> = new Set();
 
 
 
  ngOnInit(): void {
 
    this.initializeYears();
    this.fetchCalendarData();
  }
 
  initializeYears(): void {
    const startYear = this.currentYear - 5;
    const endYear = this.currentYear + 5;
    this.years = Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);
  }
 
  fetchCalendarData(): void {
    const requestData: OPCalendarGetMonthlyCalendarByCompanyRequestDTO = {
      companyId: 1,
      year: this.currentYear,
      month: this.currentMonth
    };
 
 
    // Cancel any ongoing retry before making a new API request
    if (this.latestSubscription) {
      this.latestSubscription.unsubscribe();
    }
 
    this.latestSubscription = this.calendarService.OPCalendar_getMonthlyCalendarByCompany(requestData)
      .pipe(
        catchError(error => {
          return timer(5000).pipe(
            switchMap(() => this.calendarService.OPCalendar_getMonthlyCalendarByCompany(requestData))
          );
        })
      )
      .subscribe({
        next: (res) => {
          this.calendarDays = res.items;
          this.emitMonthYearChange();
 
          this.sendOPCalender.emit(this.calendarDays);
        },
        error: (err) => {
          console.error("API call failed permanently:", err);
        }
      });
  }
 
 
  onMonthChange(month: number): void {
    if (this.isNavigationDisabled) {
      this.emitErrorMsg.emit("RegFirst");
      return;
    }
 
    clearTimeout(this.monthChangeTimeout); // Prevent multiple calls
 
    this.monthChangeTimeout = setTimeout(() => {
      this.currentMonth = month;
      if (this.currentMonth < 1) {
        this.currentMonth = 12;
        this.currentYear--;
      }
      if (this.currentMonth > 12) {
        this.currentMonth = 1;
        this.currentYear++;
      }
 
      this.fetchCalendarData();
    }, 300); // Wait 300ms before changing
  }
 
  onYearChange(year: number): void {
    if (this.isNavigationDisabled) {
      this.emitErrorMsg.emit("RegFirst");
      return;
 
    }
 
    this.currentYear = year;
    this.fetchCalendarData();
  }
 
  getEmptyPlaceholders(): number[] {
    const firstDayOfMonth = new Date(this.currentYear, this.currentMonth - 1, 1).getDay();
    return Array.from({ length: firstDayOfMonth }, (_, i) => i);
  }

  dayClick(day: OPCalendarDTO): void {
 
    if (this.canClickFutureDate(day) && this.shouldAllowClick(day) && day.isWorkingDay === 1) {
      const index = this.selectedDays.findIndex(selected => selected.date === day.date);
 
      if (this.allowMultipleSelection) {
        // Toggle selection in multi-select mode
        if (index !== -1) {
          this.selectedDays.splice(index, 1);
        } else {
          this.selectedDays.push(day);
        }
      } else {
        // Single select: replace the selected day
        if (index === -1 || this.selectedDays.length > 1) {
          this.selectedDays = [day];
        } else {
          // If it's already selected, toggle it off
          this.selectedDays = [];
        }
      }

      this.onDayClick.emit(day); // Also emit the clicked day if needed
    }
  }
 
  isToday(day: any): boolean {
    if (this.isHighlightTodaysDay.enabled) {
      const today = new Date();
      return day.day === today.getDate() &&
        day.month === today.getMonth() + 1 &&
        day.year === today.getFullYear();
    }
    return false;
  }

  getTodayHighlightStyle(): string {
    return this.isHighlightTodaysDay.enabled ? this.isHighlightTodaysDay.style : '';
  }
 
  private emitMonthYearChange(): void {
    this.onMonthYearChange.emit({ year: this.currentYear, month: this.currentMonth });
  }
 
 
  isSelected(day: OPCalendarDTO): boolean {
    return this.selectedDays.some(selected => selected.date === day.date);
  }
 
 
  ngOnChanges() {

  }
 
 
  isPresent(day: OPCalendarDTO): boolean {
    if (this.showIsPresent) {
      let dayString = new Date(day.date).toISOString().split('T')[0];
      return this.attendanceDates.has(dayString);
    } else {
      return false;
    }
  }
 
  canClickFutureDate(day: OPCalendarDTO): boolean {
    const today = new Date();
    const dateToCheck = new Date(day.date);
 
    // If allowFutureDates is true, allow all working days
    if (this.allowClickingFutureDates) return day.isWorkingDay === 1;
 
    // Otherwise, allow only if date is <= today
    return day.isWorkingDay === 1 && dateToCheck <= today;
  }
 
  giveOrangeBorder(day: OPCalendarDTO): boolean {
    if (this.showOrangeBorder) {
      let dayString = new Date(day.date).toISOString().split('T')[0];
      return this.hasOrangeBorderDates.some(d =>
        new Date(d.date).toISOString().split('T')[0] === dayString
      );
    } else {
      return false;
    }
  }
 
  private shouldAllowClick(day: OPCalendarDTO): boolean {
    if (!this.hasTocheckRG) {
      return true; // Skip RG check entirely
    }
    return this.giveOrangeBorder(day); // Only allow if marked with orange border
  }
 
  giveTimeIcon(day: OPCalendarDTO) {

  }
 
  giveTickMark(day: OPCalendarDTO) {

  }
 
  giveCrossMark(day: OPCalendarDTO) {

  }
 
  notiBtnClick() {
    this.onNotiBtnClicked.emit();
  }
 
  getUTCDateString(dateInput: string | Date): string | null {
    try {
        // Attempt to create a date object. This might interpret locally if only 'YYYY-MM-DD'.
        const d = new Date(dateInput);

        // Crucially, create a *new* Date object using UTC components
        // This avoids local timezone interpretation issues of the initial parse.
        const utcDate = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));

        // Check if the UTC date is valid before formatting
        if (isNaN(utcDate.getTime())) {
             return null;
         }

        // Format to YYYY-MM-DD based on UTC values
        return utcDate.toISOString().split('T')[0];

    } catch (e) {
        return null;
    }
}

// ---- Inside your component ----

getIndicatorCountForDay(day: OPCalendarDTO): number | null {
    if (!this.showNumberIndicatorData) return null;

    const dayStr = this.getUTCDateString(day.date);
    if (dayStr === null) return null; // Handle parsing error

    const meeting = this.numberIndicatorData.find(m => {
        const mStr = this.getUTCDateString(m.date);
        return mStr !== null && mStr === dayStr;
    });

    return meeting ? meeting.count : null;
}

getMeetings(day: OPCalendarDTO): string[] {
    if (!this.showNumberIndicatorData) return [];

    const dayStr = this.getUTCDateString(day.date);
     if (dayStr === null) return []; // Handle parsing error

    const meeting = this.numberIndicatorData.find(m => {
        const mStr = this.getUTCDateString(m.date);
        return mStr !== null && mStr === dayStr;
    });

    return meeting && meeting.tooltip ? meeting.tooltip : [];
}



  // This method will return a boolean to determine if the user initials should be displayed
  giveUserIcons(day: OPCalendarDTO): boolean {
    // Find the data for the given day from `initialUserIconsData`
    const userIcons = this.initialUserIconsData.find(icon => icon.date === day.date.toString());
    return !!userIcons;  // Returns true if there are user icons for the given day, false otherwise
  }

  // Method to get initials (first letter of first and last name)
getInitials(username: string): string {
  if(!username || username === null) {
      // Return a fallback value (e.g., 'XX' or an empty string) if username is undefined or null
      return 'XX';
    }
   
    // Remove extra spaces from the username (trim and collapse multiple spaces to one)
    const cleanedUsername = username.replace(/\s+/g, ' ').trim();
   
    const nameParts = cleanedUsername.split(' ');
    // If there are at least two parts, return initials of the first and last names
    if (nameParts.length > 1) {
      return nameParts[0][0].toUpperCase() + nameParts[1][0].toUpperCase(); // First letter of first and last name
    }
   
    // If there's only one part (e.g., single name), return the first two letters
    return cleanedUsername.substring(0, 2).toUpperCase();
  }

// This method will return the tooltip for the given day as a single string
getToolTipForDay(day: OPCalendarDTO): string {
  const userIcons = this.initialUserIconsData.find(icon => icon.date === day.date.toString());
  if (userIcons) {
    // Join the tooltip array into a single string, separating each entry with a newline
    return userIcons.tooltip!.join('\n');
  }
  return '';  // Return an empty string if no tooltip exists for the given day
}

// Helper function to generate a color from a username
generateColorForUser(username: string): string {
  const hash = Array.from(username).reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const color = `hsl(${hash % 280}, 70%, 60%)`; // Generating a color based on the hash value
  return color;
}

// Method to get the usernames for a given day, limiting to 3 with +X on the 3rd icon
getUserNamesForDayWithLimit(day: OPCalendarDTO): string[] {
  const userIcons = this.initialUserIconsData.find(icon => icon.date === day.date.toString());
  if (userIcons) {
    const usernames = userIcons.username;
    if (usernames.length > 2) {
      // If there are more than 3 usernames, return the first 3 and show a "+X" for the remaining ones
      const additionalCount = usernames.length - 2;
      return [...usernames.slice(0, 2), `+${additionalCount}`];
    } else {
      return usernames;
    }
  }
  return [];
}

// Get the color for a specific user
getUserColor(username: string): string {
  return this.generateColorForUser(username);
}


}